import React, { useEffect, useState } from 'react';
import { Company, PricingTier, Venue } from '@/types';
import { useCompanyMutations } from '@features/admin/data';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { validateUkPhone } from '@/lib/validators';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { MessageSquare } from 'lucide-react';

type CompanyStatus = Company['status'];
const isCompanyStatus = (value: string): value is CompanyStatus =>
  value === 'pending' || value === 'active' || value === 'on_hold' || value === 'rejected';

const normalizeEditableCompany = (company: Company): Company => ({
  ...company,
  paymentPreference: company.paymentPreference || 'invoice',
  tier: company.tier || 'bronze',
  status: company.status || 'active',
  requirePo: Boolean(company.requirePo)
});

interface CompanyEditorProps {
  company: Company | null;
  venues: Venue[];
  onClose: () => void;
  onSaved: () => void;
}

export const CompanyEditor: React.FC<CompanyEditorProps> = ({
  company,
  venues,
  onClose,
  onSaved
}) => {
  const companyMutations = useCompanyMutations();

  const [editingCompany, setEditingCompany] = useState<Company | null>(
    company
      ? normalizeEditableCompany(company)
      : ({
          id: '',
          name: '',
          contactPerson: '',
          email: '',
          address: '',
          status: 'active',
          tier: 'bronze',
          paymentPreference: 'invoice',
          requirePo: false,
          allowedVenueIds: venues.map((v) => v.id),
          createdAt: '',
          changeLogs: []
        } as Company)
  );
  const [companyPhoneTouched, setCompanyPhoneTouched] = useState(false);

  // Sync internal draft when parent company prop changes
  useEffect(() => {
    if (company) {
      setEditingCompany(normalizeEditableCompany(company));
    }
    setCompanyPhoneTouched(false);
  }, [company?.id]);

  const toggleVenueRestriction = (venueId: string) => {
    if (!editingCompany) return;
    const currentAllowed = editingCompany.allowedVenueIds || [];
    const newAllowed = currentAllowed.includes(venueId)
      ? currentAllowed.filter((id) => id !== venueId)
      : [...currentAllowed, venueId];
    setEditingCompany({ ...editingCompany, allowedVenueIds: newAllowed });
  };

  if (!editingCompany) return null;

  const editingPhoneValue = String(editingCompany.phone || '').trim();
  const showCompanyPhoneError =
    companyPhoneTouched && editingPhoneValue && !validateUkPhone(editingPhoneValue).valid;

  const handleSave = async () => {
    try {
      const rawPhone = String(editingCompany.phone || '').trim();
      const phoneResult = rawPhone ? validateUkPhone(rawPhone) : null;
      if (phoneResult && !phoneResult.valid) {
        setCompanyPhoneTouched(true);
        showAlertModal('Please enter a valid UK phone number.');
        return;
      }
      const normalizedPhone = phoneResult && phoneResult.valid ? phoneResult.normalized : '';

      if (editingCompany.id) {
        const payload: Company = {
          ...editingCompany,
          phone: normalizedPhone,
          status: editingCompany.status || 'active',
          tier: editingCompany.tier || 'bronze',
          paymentPreference: editingCompany.paymentPreference || 'invoice',
          requirePo: Boolean(editingCompany.requirePo)
        };
        await companyMutations.update(payload);
      } else {
        await companyMutations.create({
          name: editingCompany.name,
          contactPerson: editingCompany.contactPerson,
          email: editingCompany.email,
          address: editingCompany.address,
          id: createId(),
          createdAt: new Date().toISOString(),
          phone: normalizedPhone,
          paymentPreference: editingCompany.paymentPreference || 'invoice',
          tier: editingCompany.tier || 'bronze',
          status: editingCompany.status || 'active',
          requirePo: Boolean(editingCompany.requirePo),
          allowedVenueIds: editingCompany.allowedVenueIds,
          staffNotes: editingCompany.staffNotes,
          changeLogs: []
        } as Company);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to save company changes.'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg text-slate-900 mb-4">
          {editingCompany.id ? 'Edit Company' : 'Add Company'}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Company Name</label>
            <input
              className="w-full border p-2 rounded bg-white"
              value={editingCompany.name}
              onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Contact Person</label>
            <input
              className="w-full border p-2 rounded bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              value={editingCompany.contactPerson}
              disabled={Boolean(editingCompany.id) && editingCompany.status === 'active'}
              onChange={(e) =>
                setEditingCompany({ ...editingCompany, contactPerson: e.target.value })
              }
            />
            {Boolean(editingCompany.id) && editingCompany.status === 'active' && (
              <p className="text-[10px] text-slate-400 mt-1">
                To change the primary contact, open <strong>View Team</strong> and use{' '}
                <strong>Set as Primary Contact</strong> on the desired team member.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Company Address</label>
            <input
              className="w-full border p-2 rounded bg-white"
              value={editingCompany.address}
              onChange={(e) => setEditingCompany({ ...editingCompany, address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Phone Number</label>
            <input
              className="w-full border p-2 rounded bg-white"
              value={editingCompany.phone || ''}
              onChange={(e) => setEditingCompany({ ...editingCompany, phone: e.target.value })}
              onBlur={(e) => {
                const value = e.target.value.trim();
                setCompanyPhoneTouched(true);
                const phoneResult = value ? validateUkPhone(value) : null;
                if (phoneResult && phoneResult.valid) {
                  setEditingCompany({ ...editingCompany, phone: phoneResult.normalized });
                }
              }}
              placeholder="e.g. 01234 567890"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              UK numbers only, e.g. 07123 456789 or +44 7123 456789.
            </p>
            {showCompanyPhoneError && (
              <p className="mt-1 text-xs font-semibold text-red-600">
                Enter a valid UK phone number.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
            <input
              className="w-full border p-2 rounded bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              value={editingCompany.email}
              disabled={Boolean(editingCompany.id) && editingCompany.status === 'active'}
              onChange={(e) => setEditingCompany({ ...editingCompany, email: e.target.value })}
            />
            {Boolean(editingCompany.id) && editingCompany.status === 'active' && (
              <p className="text-[10px] text-slate-400 mt-1">
                Primary contact email cannot be edited here. Open <strong>View Team</strong>, add
                the new contact as a team member, then use <strong>Set as Primary Contact</strong>{' '}
                to transfer.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Pricing Tier</label>
              <select
                className="w-full border rounded p-2 bg-white"
                value={editingCompany.tier}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, tier: e.target.value as PricingTier })
                }
              >
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
                <option value="diamond">Diamond</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Payment Method
              </label>
              <select
                className="w-full border rounded p-2 bg-white"
                value={editingCompany.paymentPreference || 'invoice'}
                onChange={(e) => {
                  const val = e.target.value as 'invoice' | 'card';
                  setEditingCompany({
                    ...editingCompany,
                    paymentPreference: val,
                    requirePo: val === 'card' ? false : editingCompany.requirePo
                  });
                }}
              >
                <option value="invoice">Invoice</option>
                <option value="card">Card / Charge</option>
              </select>
            </div>
          </div>

          {editingCompany.paymentPreference === 'invoice' && (
            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 flex justify-between items-center">
              <div>
                <div className="text-xs font-bold text-blue-900">Force PO Number</div>
                <p className="text-[10px] text-blue-500">Prevent bookings without a reference.</p>
              </div>
              <input
                type="checkbox"
                className="w-5 h-5 text-teal-600 rounded"
                checked={!!editingCompany.requirePo}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, requirePo: e.target.checked })
                }
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
            <select
              className="w-full border rounded p-2 bg-white"
              value={editingCompany.status}
              onChange={(e) => {
                const value = e.target.value;
                if (isCompanyStatus(value)) {
                  setEditingCompany({ ...editingCompany, status: value });
                }
              }}
            >
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <label className="block text-xs font-bold text-amber-900 mb-1.5 flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> Internal Staff Notes
            </label>
            <textarea
              className="w-full border p-2 rounded bg-white text-xs min-h-[100px] outline-none focus:ring-1 focus:ring-amber-500"
              placeholder="Private notes about this company account... (Pricing deals, key contacts, billing nuances)"
              value={editingCompany.staffNotes || ''}
              onChange={(e) => setEditingCompany({ ...editingCompany, staffNotes: e.target.value })}
            />
            <p className="text-[9px] text-amber-600 mt-1 italic">
              Visible to staff only. The company cannot see these.
            </p>
          </div>

          <div className="pt-2">
            <label className="block text-xs font-medium text-slate-500 mb-2">
              Allowed Locations
            </label>
            <div className="border rounded-lg max-h-40 overflow-y-auto p-2 bg-slate-50 space-y-1">
              {venues.length > 0 ? (
                venues.map((venue) => (
                  <label
                    key={venue.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1.5 rounded"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-teal-600 rounded"
                      checked={
                        editingCompany.allowedVenueIds && editingCompany.allowedVenueIds.length > 0
                          ? editingCompany.allowedVenueIds.includes(venue.id)
                          : true
                      }
                      onChange={() => toggleVenueRestriction(venue.id)}
                    />
                    <span className="text-sm text-slate-700">
                      {venue.name}, {venue.city}
                    </span>
                  </label>
                ))
              ) : (
                <p className="text-xs text-slate-400 p-2">No venues configured in system.</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-slate-900 text-white px-4 py-2 rounded hover:bg-slate-800"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
